import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

function Analytics() {
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [scholarshipApplications, setScholarshipApplications] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role?.startsWith('admin-')) {
      fetchAllData();
    }
  }, [user]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      console.log('Starting to fetch analytics data...');

      // Fetch contact submissions
      const contactQuery = query(collection(db, 'contactSubmissions'), orderBy('timestamp', 'desc'));
      const contactSnapshot = await getDocs(contactQuery);
      const contactData = contactSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContactSubmissions(contactData);
      console.log('Fetched contacts:', contactData.length);

      // Fetch scholarship applications
      const scholarshipQuery = query(collection(db, 'scholarship_applications'), orderBy('appliedAt', 'desc'));
      const scholarshipSnapshot = await getDocs(scholarshipQuery);
      const scholarshipData = scholarshipSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setScholarshipApplications(scholarshipData);
      console.log('Fetched scholarships:', scholarshipData.length);

      // Fetch blog posts
      const blogQuery = query(collection(db, 'blog_posts'), orderBy('publishedAt', 'desc'));
      const blogSnapshot = await getDocs(blogQuery);
      const blogData = blogSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBlogPosts(blogData);
      console.log('Fetched blog posts:', blogData.length);

      // Fetch courses
      const coursesQuery = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
      const coursesSnapshot = await getDocs(coursesQuery);
      const coursesData = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(coursesData);
      console.log('Fetched courses:', coursesData.length);

      // Fetch quizzes
      const quizzesQuery = query(collection(db, 'quizzes'), orderBy('createdAt', 'desc'));
      const quizzesSnapshot = await getDocs(quizzesQuery);
      const quizzesData = quizzesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuizzes(quizzesData);
      console.log('Fetched quizzes:', quizzesData.length);

      // Fetch course enrollments from the root collection if it exists, otherwise use empty array
      let allEnrollments = [];
      try {
        const enrollmentsQuery = query(collection(db, 'courseEnrollments'));
        const enrollmentsSnap = await getDocs(enrollmentsQuery);
        allEnrollments = enrollmentsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Fetched course enrollments:', allEnrollments.length);
      } catch (e) {
        console.log('Could not fetch courseEnrollments collection, using empty array:', e.message);
        allEnrollments = [];
      }
      setCourseEnrollments(allEnrollments);

      // Fetch quiz results from the root collection if it exists, otherwise use empty array
      let allResults = [];
      try {
        const resultsQuery = query(collection(db, 'quizResults'));
        const resultsSnap = await getDocs(resultsQuery);
        allResults = resultsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Fetched quiz results:', allResults.length);
      } catch (e) {
        console.log('Could not fetch quizResults collection, using empty array:', e.message);
        allResults = [];
      }
      setQuizResults(allResults);

      console.log('All data fetched successfully');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error fetching analytics data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Contact Management Analytics
  const getContactStatusDistribution = () => {
    const statusCounts = contactSubmissions.reduce((acc, submission) => {
      acc[submission.status || 'pending'] = (acc[submission.status || 'pending'] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(statusCounts).map(status => status.replace('_', ' ')),
      datasets: [{
        data: Object.values(statusCounts),
        backgroundColor: [
          'rgba(130, 88, 18, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
        ],
        borderColor: [
          'rgba(130, 88, 18, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      }]
    };
  };

  const getContactSubmissionTrends = () => {
    const submissionsByDate = contactSubmissions.reduce((acc, submission) => {
      const date = submission.timestamp?.toDate().toLocaleDateString() || 'Unknown';
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(submissionsByDate).sort();

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Submissions',
        data: sortedDates.map(date => submissionsByDate[date]),
        borderColor: 'rgba(130, 88, 18, 1)',
        backgroundColor: 'rgba(130, 88, 18, 0.2)',
        tension: 0.4,
      }]
    };
  };

  // Scholarship Management Analytics
  const getScholarshipStatusDistribution = () => {
    const statusCounts = scholarshipApplications.reduce((acc, application) => {
      acc[application.status || 'pending'] = (acc[application.status || 'pending'] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(statusCounts).map(status => status.replace('_', ' ')),
      datasets: [{
        data: Object.values(statusCounts),
        backgroundColor: [
          'rgba(130, 88, 18, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
        ],
        borderColor: [
          'rgba(130, 88, 18, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      }]
    };
  };

  const getScholarshipApplicationTrends = () => {
    const applicationsByDate = scholarshipApplications.reduce((acc, application) => {
      // Try both possible timestamp fields
      const timestamp = application.appliedAt || application.timestamp;
      let date = 'Unknown';
      
      if (timestamp) {
        try {
          // Handle both Firestore timestamps and ISO strings
          if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate().toLocaleDateString();
          } else if (typeof timestamp === 'string') {
            date = new Date(timestamp).toLocaleDateString();
          }
        } catch (e) {
          date = 'Unknown';
        }
      }
      
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(applicationsByDate).sort();

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Applications',
        data: sortedDates.map(date => applicationsByDate[date]),
        borderColor: 'rgba(130, 88, 18, 1)',
        backgroundColor: 'rgba(130, 88, 18, 0.2)',
        tension: 0.4,
      }]
    };
  };

  // Socio-demographic Analytics for Scholarships
  const getGenderDistribution = () => {
    const genderCounts = scholarshipApplications.reduce((acc, app) => {
      // Try multiple field names
      const gender = app.gender || app.Gender || app.applicantGender || 'Not specified';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(genderCounts),
      datasets: [{
        data: Object.values(genderCounts),
        backgroundColor: [
          'rgba(255, 107, 107, 0.6)',
          'rgba(111, 134, 214, 0.6)',
          'rgba(100, 200, 100, 0.6)',
          'rgba(255, 193, 7, 0.6)',
        ],
        borderColor: [
          'rgba(255, 107, 107, 1)',
          'rgba(111, 134, 214, 1)',
          'rgba(100, 200, 100, 1)',
          'rgba(255, 193, 7, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getAgeGroupDistribution = () => {
    const ageGroups = {
      '16-20': 0,
      '21-25': 0,
      '26-30': 0,
      '31-35': 0,
      '36-40': 0,
      '40+': 0,
    };

    scholarshipApplications.forEach(app => {
      const age = app.age || app.Age || app.applicantAge;
      if (age) {
        const parsedAge = typeof age === 'number' ? age : parseInt(age);
        if (!isNaN(parsedAge)) {
          if (parsedAge <= 20) ageGroups['16-20']++;
          else if (parsedAge <= 25) ageGroups['21-25']++;
          else if (parsedAge <= 30) ageGroups['26-30']++;
          else if (parsedAge <= 35) ageGroups['31-35']++;
          else if (parsedAge <= 40) ageGroups['36-40']++;
          else ageGroups['40+']++;
        }
      }
    });

    return {
      labels: Object.keys(ageGroups),
      datasets: [{
        label: 'Age Groups',
        data: Object.values(ageGroups),
        backgroundColor: 'rgba(130, 88, 18, 0.6)',
        borderColor: 'rgba(130, 88, 18, 1)',
        borderWidth: 2,
      }]
    };
  };

  const getLocationDistribution = () => {
    const locationCounts = scholarshipApplications.reduce((acc, app) => {
      // Try multiple field names for location
      const location = app.state || app.State || app.region || app.Region || app.location || app.Location || app.institution || 'Unknown';
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    const sortedLocations = Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      labels: sortedLocations.map(([loc]) => loc),
      datasets: [{
        label: 'Applications',
        data: sortedLocations.map(([, count]) => count),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
      }]
    };
  };

  const getAcademicLevelDistribution = () => {
    const academicLevels = scholarshipApplications.reduce((acc, app) => {
      // Try multiple field names
      const level = app.level || app.Level || app.academicLevel || app.educationLevel || app.Education || 'Not specified';
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(academicLevels),
      datasets: [{
        data: Object.values(academicLevels),
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(201, 203, 207, 0.6)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(201, 203, 207, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getFinancialNeedDistribution = () => {
    const needCounts = scholarshipApplications.reduce((acc, app) => {
      // Try multiple field names
      const need = app.financialNeed || app.FinancialNeed || app.needAssessment || 'Not assessed';
      acc[need] = (acc[need] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(needCounts),
      datasets: [{
        data: Object.values(needCounts),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(54, 162, 235, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getEmploymentStatusDistribution = () => {
    const employmentCounts = scholarshipApplications.reduce((acc, app) => {
      // Try multiple field names
      const status = app.employmentStatus || app.EmploymentStatus || app.employment || 'Not specified';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(employmentCounts),
      datasets: [{
        data: Object.values(employmentCounts),
        backgroundColor: [
          'rgba(130, 88, 18, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(130, 88, 18, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getScholarshipComprehensiveStats = () => {
    const total = scholarshipApplications.length;
    const approved = scholarshipApplications.filter(s => s.status === 'approved').length;
    const rejected = scholarshipApplications.filter(s => s.status === 'rejected').length;
    const pending = scholarshipApplications.filter(s => s.status === 'pending').length;
    
    const approvalRate = total > 0 ? ((approved / total) * 100).toFixed(1) : 0;
    const rejectionRate = total > 0 ? ((rejected / total) * 100).toFixed(1) : 0;
    
    const highNeed = scholarshipApplications.filter(s => {
      const need = s.financialNeed || s.FinancialNeed || '';
      return need.toLowerCase().includes('high') || need.toLowerCase().includes('critical');
    }).length;
    
    const employed = scholarshipApplications.filter(s => {
      const emp = s.employmentStatus || s.EmploymentStatus || '';
      return emp.toLowerCase().includes('employed') || emp.toLowerCase().includes('part-time');
    }).length;
    
    const femaleApplicants = scholarshipApplications.filter(s => {
      const gender = s.gender || s.Gender || '';
      return gender.toLowerCase().includes('female') || gender.toLowerCase().includes('woman') || gender === 'F';
    }).length;
    
    const maleApplicants = scholarshipApplications.filter(s => {
      const gender = s.gender || s.Gender || '';
      return gender.toLowerCase().includes('male') || gender.toLowerCase().includes('man') || gender === 'M';
    }).length;

    return {
      total,
      approved,
      rejected,
      pending,
      approvalRate,
      rejectionRate,
      highNeed,
      employed,
      femaleApplicants,
      maleApplicants,
    };
  };

  // Course Management Analytics
  const getCourseStats = () => {
    const totalCourses = courses.length;
    const totalEnrollments = courseEnrollments.length;
    const avgEnrollmentsPerCourse = totalCourses > 0 ? (totalEnrollments / totalCourses).toFixed(1) : 0;
    
    const coursesByCategory = courses.reduce((acc, course) => {
      const category = course.category || course.courseCategory || 'Uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    const completedEnrollments = courseEnrollments.filter(e => e.progress === 100 || e.completed).length;
    const completionRate = totalEnrollments > 0 ? ((completedEnrollments / totalEnrollments) * 100).toFixed(1) : 0;

    return {
      totalCourses,
      totalEnrollments,
      avgEnrollmentsPerCourse,
      completedEnrollments,
      completionRate,
      coursesByCategory
    };
  };

  const getCourseDistributionByCategory = () => {
    const stats = getCourseStats();
    return {
      labels: Object.keys(stats.coursesByCategory),
      datasets: [{
        data: Object.values(stats.coursesByCategory),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getCourseEnrollmentTrends = () => {
    const enrollmentsByDate = courseEnrollments.reduce((acc, enrollment) => {
      const timestamp = enrollment.enrolledAt || enrollment.createdAt;
      let date = 'Unknown';
      
      if (timestamp) {
        try {
          if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate().toLocaleDateString();
          } else if (typeof timestamp === 'string') {
            date = new Date(timestamp).toLocaleDateString();
          }
        } catch (e) {
          date = 'Unknown';
        }
      }
      
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(enrollmentsByDate).sort();

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Enrollments',
        data: sortedDates.map(date => enrollmentsByDate[date]),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      }]
    };
  };

  const getCourseCompletionStatus = () => {
    const inProgress = courseEnrollments.filter(e => e.progress && e.progress > 0 && e.progress < 100).length;
    const completed = courseEnrollments.filter(e => e.progress === 100 || e.completed).length;
    const notStarted = courseEnrollments.filter(e => !e.progress || e.progress === 0).length;

    return {
      labels: ['Not Started', 'In Progress', 'Completed'],
      datasets: [{
        data: [notStarted, inProgress, completed],
        backgroundColor: [
          'rgba(201, 203, 207, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
        ],
        borderColor: [
          'rgba(201, 203, 207, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  // Quiz Management Analytics
  const getQuizStats = () => {
    const totalQuizzes = quizzes.length;
    const totalAttempts = quizResults.length;
    const avgAttemptsPerQuiz = totalQuizzes > 0 ? (totalAttempts / totalQuizzes).toFixed(1) : 0;
    
    const quizzesBySubject = quizzes.reduce((acc, quiz) => {
      const subject = quiz.subject || quiz.category || 'General';
      acc[subject] = (acc[subject] || 0) + 1;
      return acc;
    }, {});

    const avgScore = totalAttempts > 0 
      ? (quizResults.reduce((sum, result) => sum + (result.score || 0), 0) / totalAttempts).toFixed(1)
      : 0;

    const passedAttempts = quizResults.filter(r => (r.score || 0) >= 70).length;
    const passRate = totalAttempts > 0 ? ((passedAttempts / totalAttempts) * 100).toFixed(1) : 0;

    return {
      totalQuizzes,
      totalAttempts,
      avgAttemptsPerQuiz,
      avgScore,
      passRate,
      passedAttempts,
      quizzesBySubject
    };
  };

  const getQuizDistributionBySubject = () => {
    const stats = getQuizStats();
    return {
      labels: Object.keys(stats.quizzesBySubject),
      datasets: [{
        data: Object.values(stats.quizzesBySubject),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 2,
      }]
    };
  };

  const getQuizScoreDistribution = () => {
    const scoreRanges = {
      '0-20': 0,
      '21-40': 0,
      '41-60': 0,
      '61-80': 0,
      '81-100': 0,
    };

    quizResults.forEach(result => {
      const score = result.score || 0;
      if (score <= 20) scoreRanges['0-20']++;
      else if (score <= 40) scoreRanges['21-40']++;
      else if (score <= 60) scoreRanges['41-60']++;
      else if (score <= 80) scoreRanges['61-80']++;
      else scoreRanges['81-100']++;
    });

    return {
      labels: Object.keys(scoreRanges),
      datasets: [{
        label: 'Attempts',
        data: Object.values(scoreRanges),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 2,
      }]
    };
  };

  const getQuizAttemptTrends = () => {
    const attemptsByDate = quizResults.reduce((acc, result) => {
      const timestamp = result.completedAt || result.createdAt;
      let date = 'Unknown';
      
      if (timestamp) {
        try {
          if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate().toLocaleDateString();
          } else if (typeof timestamp === 'string') {
            date = new Date(timestamp).toLocaleDateString();
          }
        } catch (e) {
          date = 'Unknown';
        }
      }
      
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(attemptsByDate).sort();

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Attempts',
        data: sortedDates.map(date => attemptsByDate[date]),
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        tension: 0.4,
      }]
    };
  };

  // Blog Management Analytics
  const getBlogPostStats = () => {
    const postsByCategory = blogPosts.reduce((acc, post) => {
      acc[post.category || 'Uncategorized'] = (acc[post.category || 'Uncategorized'] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(postsByCategory),
      datasets: [{
        data: Object.values(postsByCategory),
        backgroundColor: [
          'rgba(130, 88, 18, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
        ],
        borderColor: [
          'rgba(130, 88, 18, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      }]
    };
  };

  const getBlogPostTrends = () => {
    const postsByDate = blogPosts.reduce((acc, post) => {
      const publishedAt = post.publishedAt?.toDate ? post.publishedAt.toDate() : post.publishedAt ? new Date(post.publishedAt) : null;
      const date = publishedAt ? publishedAt.toLocaleDateString() : 'Unknown';
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(postsByDate).sort();

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Posts',
        data: sortedDates.map(date => postsByDate[date]),
        borderColor: 'rgba(130, 88, 18, 1)',
        backgroundColor: 'rgba(130, 88, 18, 0.2)',
        tension: 0.4,
      }]
    };
  };

  // Overview Statistics
  const getOverviewStats = () => {
    const totalContacts = contactSubmissions.length;
    const pendingContacts = contactSubmissions.filter(s => s.status === 'pending').length;
    const totalScholarships = scholarshipApplications.length;
    const pendingScholarships = scholarshipApplications.filter(s => s.status === 'pending').length;
    const totalBlogPosts = blogPosts.length;
    const recentBlogPosts = blogPosts.filter(post => {
      const publishedAt = post.publishedAt?.toDate ? post.publishedAt.toDate() : post.publishedAt ? new Date(post.publishedAt) : null;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return publishedAt && publishedAt > thirtyDaysAgo;
    }).length;

    return {
      totalContacts,
      pendingContacts,
      totalScholarships,
      pendingScholarships,
      totalBlogPosts,
      recentBlogPosts
    };
  };

  if (!user?.role?.startsWith('admin-')) {
    return <div className="p-4 text-red-600">You do not have permission to access this page.</div>;
  }

  if (loading) return <div className="p-4">Loading analytics...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  const overviewStats = getOverviewStats();

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[rgb(130,88,18)]">Analytics Dashboard</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded ${
              activeTab === 'overview' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 rounded ${
              activeTab === 'contacts' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('scholarships')}
            className={`px-4 py-2 rounded ${
              activeTab === 'scholarships' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Scholarships
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            className={`px-4 py-2 rounded ${
              activeTab === 'blog' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Blog
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-2 rounded ${
              activeTab === 'courses' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Courses
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            className={`px-4 py-2 rounded ${
              activeTab === 'quizzes' 
                ? 'bg-[rgb(130,88,18)] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Quizzes
          </button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Overview Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-gray-500 text-sm">Contact Submissions</h3>
              <p className="text-2xl font-bold text-[rgb(130,88,18)]">{overviewStats.totalContacts}</p>
              <p className="text-sm text-gray-600">{overviewStats.pendingContacts} pending</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-gray-500 text-sm">Scholarship Applications</h3>
              <p className="text-2xl font-bold text-[rgb(130,88,18)]">{overviewStats.totalScholarships}</p>
              <p className="text-sm text-gray-600">{overviewStats.pendingScholarships} pending</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-gray-500 text-sm">Blog Posts</h3>
              <p className="text-2xl font-bold text-[rgb(130,88,18)]">{overviewStats.totalBlogPosts}</p>
              <p className="text-sm text-gray-600">{overviewStats.recentBlogPosts} in last 30 days</p>
            </div>
          </div>

          {/* Overview Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Contact Status Distribution</h3>
              <div style={{ height: '300px' }}>
                <Doughnut 
                  data={getContactStatusDistribution()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Scholarship Status Distribution</h3>
              <div style={{ height: '300px' }}>
                <Doughnut 
                  data={getScholarshipStatusDistribution()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Contact Submission Trends</h3>
              <div style={{ height: '300px' }}>
                <Line 
                  data={getContactSubmissionTrends()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Contact Status Distribution</h3>
              <div style={{ height: '300px' }}>
                <Pie 
                  data={getContactStatusDistribution()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'scholarships' && (
        <div className="space-y-8">
          {/* Comprehensive Statistics Cards */}
          <div>
            <h3 className="text-xl font-bold text-[rgb(130,88,18)] mb-4">Overview Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(() => {
                const stats = getScholarshipComprehensiveStats();
                return (
                  <>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="text-gray-500 text-sm font-semibold">Total Applications</h4>
                      <p className="text-3xl font-bold text-[rgb(130,88,18)]">{stats.total}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg shadow border-l-4 border-green-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Approved</h4>
                      <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
                      <p className="text-xs text-gray-600">{stats.approvalRate}% approval rate</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg shadow border-l-4 border-red-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Rejected</h4>
                      <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
                      <p className="text-xs text-gray-600">{stats.rejectionRate}% rejection rate</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg shadow border-l-4 border-yellow-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Pending</h4>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg shadow border-l-4 border-blue-500">
                      <h4 className="text-gray-500 text-sm font-semibold">High Financial Need</h4>
                      <p className="text-3xl font-bold text-blue-600">{stats.highNeed}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg shadow border-l-4 border-purple-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Employed Applicants</h4>
                      <p className="text-3xl font-bold text-purple-600">{stats.employed}</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg shadow border-l-4 border-pink-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Female Applicants</h4>
                      <p className="text-3xl font-bold text-pink-600">{stats.femaleApplicants}</p>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg shadow border-l-4 border-indigo-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Male Applicants</h4>
                      <p className="text-3xl font-bold text-indigo-600">{stats.maleApplicants}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Application Trends and Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Application Trends Over Time</h3>
              <div style={{ height: '300px' }}>
                <Line 
                  data={getScholarshipApplicationTrends()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Application Status Distribution</h3>
              <div style={{ height: '300px' }}>
                <Doughnut 
                  data={getScholarshipStatusDistribution()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>

          {/* Socio-demographic Analytics */}
          <div>
            <h3 className="text-xl font-bold text-[rgb(130,88,18)] mb-4">Socio-Demographic Analysis</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Gender Distribution</h3>
                <div style={{ height: '300px' }}>
                  <Doughnut 
                    data={getGenderDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Age Group Distribution</h3>
                <div style={{ height: '300px' }}>
                  <Bar 
                    data={getAgeGroupDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Academic Level Distribution</h3>
                <div style={{ height: '300px' }}>
                  <Pie 
                    data={getAcademicLevelDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Employment Status</h3>
                <div style={{ height: '300px' }}>
                  <Doughnut 
                    data={getEmploymentStatusDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow lg:col-span-2">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Top 10 Geographic Distribution</h3>
                <div style={{ height: '300px' }}>
                  <Bar 
                    data={getLocationDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow lg:col-span-2">
                <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Financial Need Assessment</h3>
                <div style={{ height: '300px' }}>
                  <Bar 
                    data={getFinancialNeedDistribution()} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'blog' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Blog Post Trends</h3>
              <div style={{ height: '300px' }}>
                <Line 
                  data={getBlogPostTrends()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Blog Posts by Category</h3>
              <div style={{ height: '300px' }}>
                <Pie 
                  data={getBlogPostStats()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'courses' && (
        <div className="space-y-8">
          {/* Course Statistics Cards */}
          <div>
            <h3 className="text-xl font-bold text-[rgb(130,88,18)] mb-4">Overview Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(() => {
                const stats = getCourseStats();
                return (
                  <>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="text-gray-500 text-sm font-semibold">Total Courses</h4>
                      <p className="text-3xl font-bold text-[rgb(130,88,18)]">{stats.totalCourses}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg shadow border-l-4 border-blue-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Total Enrollments</h4>
                      <p className="text-3xl font-bold text-blue-600">{stats.totalEnrollments}</p>
                      <p className="text-xs text-gray-600">{stats.avgEnrollmentsPerCourse} avg per course</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg shadow border-l-4 border-green-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Completed</h4>
                      <p className="text-3xl font-bold text-green-600">{stats.completedEnrollments}</p>
                      <p className="text-xs text-gray-600">{stats.completionRate}% completion rate</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg shadow border-l-4 border-purple-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Active Enrollments</h4>
                      <p className="text-3xl font-bold text-purple-600">{stats.totalEnrollments - stats.completedEnrollments}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Course Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Enrollment Trends</h3>
              <div style={{ height: '300px' }}>
                <Line 
                  data={getCourseEnrollmentTrends()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Completion Status</h3>
              <div style={{ height: '300px' }}>
                <Doughnut 
                  data={getCourseCompletionStatus()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow lg:col-span-2">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Courses by Category</h3>
              <div style={{ height: '300px' }}>
                <Bar 
                  data={getCourseDistributionByCategory()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'quizzes' && (
        <div className="space-y-8">
          {/* Quiz Statistics Cards */}
          <div>
            <h3 className="text-xl font-bold text-[rgb(130,88,18)] mb-4">Overview Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(() => {
                const stats = getQuizStats();
                return (
                  <>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <h4 className="text-gray-500 text-sm font-semibold">Total Quizzes</h4>
                      <p className="text-3xl font-bold text-[rgb(130,88,18)]">{stats.totalQuizzes}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg shadow border-l-4 border-blue-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Total Attempts</h4>
                      <p className="text-3xl font-bold text-blue-600">{stats.totalAttempts}</p>
                      <p className="text-xs text-gray-600">{stats.avgAttemptsPerQuiz} avg per quiz</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg shadow border-l-4 border-yellow-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Average Score</h4>
                      <p className="text-3xl font-bold text-yellow-600">{stats.avgScore}%</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg shadow border-l-4 border-green-500">
                      <h4 className="text-gray-500 text-sm font-semibold">Pass Rate</h4>
                      <p className="text-3xl font-bold text-green-600">{stats.passRate}%</p>
                      <p className="text-xs text-gray-600">{stats.passedAttempts} passed</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Quiz Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Attempt Trends</h3>
              <div style={{ height: '300px' }}>
                <Line 
                  data={getQuizAttemptTrends()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Score Distribution</h3>
              <div style={{ height: '300px' }}>
                <Bar 
                  data={getQuizScoreDistribution()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow lg:col-span-2">
              <h3 className="text-lg font-semibold text-[rgb(130,88,18)] mb-4">Quizzes by Subject</h3>
              <div style={{ height: '300px' }}>
                <Pie 
                  data={getQuizDistributionBySubject()} 
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;